import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh,
  Search,
  CheckCircle,
  Error,
  Pending,
  Cancel,
  Visibility,
  Block,
  Check,
  Payment,
  Verified
} from '@mui/icons-material';
import { api } from '../services/AuthService';
import { toast } from 'react-toastify';
import SubscriptionPaymentDialog from './SubscriptionPaymentDialog';

const Subscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanchonete, setSelectedLanchonete] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    overdue: 0,
    revenue: 0
  });

  const loadData = async () => {
    try {
      setLoading(true);
      // O endpoint e paginado e retorna apenas 10 registros por padrao.
      // A gestao financeira precisa carregar todas as lanchonetes.
      const response = await api.get('/admin/lanchonetes', { params: { limit: 1000 } });
      // Garantir que data seja um array, mesmo que a API retorne algo diferente
      const data = Array.isArray(response.data) ? response.data : (response.data?.lanchonetes || []);

      setLanchonetes(data);

      // Calcular estatísticas básicas
      const stats = data.reduce((acc, curr) => {
        acc.total++;
        if (['active', 'trialing'].includes(curr.subscription_status)) acc.active++;
        if (curr.subscription_status === 'pending') acc.pending++;
        if (curr.subscription_status === 'overdue') acc.overdue++;
        if (curr.subscription_status === 'active') acc.revenue += 59.90;
        return acc;
      }, { total: 0, active: 0, pending: 0, overdue: 0, revenue: 0 });

      setStats(stats);
    } catch (error) {
      console.error('Erro ao carregar assinaturas:', error);
      toast.error('Erro ao carregar dados financeiros');
      setLanchonetes([]); // Resetar para evitar erro de renderização
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleToggleBlock = async (id, currentStatus) => {
    try {
      await api.put(`/lanchonetes/${id}`, { active: !currentStatus });
      toast.success(currentStatus ? 'Lanchonete bloqueada' : 'Lanchonete desbloqueada');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status da lanchonete');
    }
  };

  const openPaymentDialog = (lanchonete) => {
    setPaymentTarget(lanchonete);
    setPaymentDialogOpen(true);
  };

  const handlePaymentSuccess = (payload) => {
    loadData();
    if (payload?.id || payload?.lanchonete?.id) {
      const updated = payload.lanchonete || payload;
      setSelectedLanchonete((current) => (
        current && current.id === updated.id ? { ...current, ...updated } : current
      ));
    }
  };

  const handleQuickRelease = async (lanchonete) => {
    const confirmed = window.confirm(
      `Liberar "${lanchonete.name}" manualmente?\n\nIsso conta como pagamento e atualiza o proximo vencimento em 30 dias.`
    );
    if (!confirmed) return;

    try {
      const response = await api.post(`/admin/lanchonetes/${lanchonete.id}/subscription/release`);
      toast.success(response.data?.message || 'Assinatura liberada');
      handlePaymentSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.details || error.response?.data?.error || 'Erro ao liberar assinatura');
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'active':
        return <Chip icon={<CheckCircle />} label="Ativo" color="success" size="small" />;
      case 'trialing':
        return <Chip icon={<CheckCircle />} label="Teste gratuito" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<Pending />} label="Aguardando pagamento" color="warning" size="small" />;
      case 'overdue':
        return <Chip icon={<Error />} label="Atrasado" color="error" size="small" />;
      case 'canceled':
        return <Chip icon={<Cancel />} label="Cancelado" color="default" size="small" />;
      default:
        return <Chip label={status || 'Desconhecido'} size="small" />;
    }
  };

  const filteredLanchonetes = Array.isArray(lanchonetes) ? lanchonetes.filter(l =>
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.id.toString().includes(searchTerm)
  ) : [];

  if (loading && lanchonetes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Gestão Financeira (Mercado Pago)</Typography>
        <Button startIcon={<Refresh />} variant="outlined" onClick={loadData}>Atualizar</Button>
      </Box>

      {/* Cards de Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="overline">Faturamento Mensal Estimado</Typography>
              <Typography variant="h4" fontWeight="bold">R$ {stats.revenue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Ativas / Teste gratuito</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Pendentes / Inativas</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Inadimplentes (Overdue)</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">{stats.overdue}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Lanchonetes */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar por nome ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Lanchonete</TableCell>
                <TableCell>Status Assinatura</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Status Sistema</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLanchonetes.map((l) => (
                <TableRow key={l.id} hover>
                  <TableCell>#{l.id}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{l.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.email}</Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(l.subscription_status)}</TableCell>
                  <TableCell>
                    {l.subscription_due_date ? new Date(l.subscription_due_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={l.active ? "Liberado" : "Bloqueado"}
                      color={l.active ? "success" : "error"}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Ver Detalhes">
                        <IconButton size="small" onClick={() => setSelectedLanchonete(l)}><Visibility /></IconButton>
                      </Tooltip>
                      <Tooltip title="Pagar / Pix / Cartao">
                        <IconButton size="small" color="primary" onClick={() => openPaymentDialog(l)}>
                          <Payment />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Liberar manualmente (conta como pago)">
                        <IconButton size="small" color="warning" onClick={() => handleQuickRelease(l)}>
                          <Verified />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={l.active ? "Bloquear Manualmente" : "Desbloquear Manualmente"}>
                        <IconButton
                          size="small"
                          color={l.active ? "error" : "success"}
                          onClick={() => handleToggleBlock(l.id, l.active)}
                        >
                          {l.active ? <Block /> : <Check />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLanchonetes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    Nenhuma lanchonete encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={Boolean(selectedLanchonete)}
        onClose={() => setSelectedLanchonete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalhes da assinatura</DialogTitle>
        <DialogContent dividers>
          {selectedLanchonete && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Lanchonete</Typography>
                <Typography fontWeight="700">{selectedLanchonete.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedLanchonete.email || 'Sem e-mail cadastrado'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status da assinatura</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedLanchonete.subscription_status)}</Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Proximo pagamento</Typography>
                <Typography>
                  {selectedLanchonete.subscription_due_date
                    ? new Date(selectedLanchonete.subscription_due_date).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Ultimo pagamento</Typography>
                <Typography>
                  {selectedLanchonete.last_payment_date
                    ? new Date(selectedLanchonete.last_payment_date).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Teste gratuito ate</Typography>
                <Typography>
                  {selectedLanchonete.trial_end_date
                    ? new Date(selectedLanchonete.trial_end_date).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Mercado Pago</Typography>
                <Typography>{selectedLanchonete.mp_subscription_link ? 'Link de assinatura gerado' : 'Link ainda não gerado'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Sistema</Typography>
                <Typography>{selectedLanchonete.active ? 'Liberado' : 'Bloqueado'}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" startIcon={<Payment />} onClick={() => openPaymentDialog(selectedLanchonete)}>
            Pagar / Pix / Cartao
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Verified />}
            onClick={() => handleQuickRelease(selectedLanchonete)}
          >
            Liberar manualmente
          </Button>
          <Button variant="contained" onClick={() => setSelectedLanchonete(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <SubscriptionPaymentDialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setPaymentTarget(null);
        }}
        lanchoneteId={paymentTarget?.id}
        lanchoneteName={paymentTarget?.name}
        allowManualRelease
        onSuccess={handlePaymentSuccess}
      />
    </Container>
  );
};

export default Subscriptions;
